/**
 * useTwainBridge — thin wrapper around the PCBTwainBridge localhost daemon.
 *
 * The bridge runs on every branch PC from `F:\BT\PCBTwainBridge` and exposes:
 *   GET  http://localhost:18625/health  → { version, status, sources: string[] }
 *   POST http://localhost:18625/scan    → { success, image: base64, mimeType,
 *                                           width, height }  or  { success: false, error }
 *
 * Ported from legacy SPFx `IdCapturePanel.tsx` lines 188-266 (bridge probe +
 * handleScan) and `DocumentCaptureModule.tsx` lines 455-473 (bridge health probe).
 *
 * Contracts come from `C:\Projects\retail-account-opening\PCBTwainBridge\
 * Program.cs` + `TwainBridge.cs` (READ ONLY — bridge is an on-prem C# daemon;
 * we only consume its HTTP surface).
 *
 * Lifecycle:
 *   - Probe /health on mount with a 1.5s timeout.
 *   - On mobile UA (or no navigator) → skip probe, mark status='missing'.
 *   - Default `selectedSource` = first source containing 'docketport' (case-
 *     insensitive), else first source returned.
 *   - `scan(source?)` always returns `null` when bridge is missing or the call
 *     fails — callers fall through to Camera or Upload.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const TWAIN_BRIDGE_URL = 'http://localhost:18625';
// Bumped 2026-04-24 (v0.1.49): the bridge's first /health response after a
// cold start (TWAIN session init + source enumeration) routinely lands in
// the 5-10s band — the prior 1.5s budget was timing out before the daemon
// finished waking up, surfacing "Bridge health check timed out" on the
// Scanner tab even though the tray icon showed the bridge was running.
//
// Strategy: 10s per probe × 2 retries (3 attempts total, ~30s worst-case
// but usually <1s on the warm path) keeps the warm-state UX instant while
// giving cold starts the headroom the installer's Step-VerifyHealth uses.
const HEALTH_TIMEOUT_MS = 10000;
const HEALTH_RETRY_COUNT = 2;
const HEALTH_RETRY_BACKOFF_MS = 500;

export type TwainBridgeStatusKind = 'checking' | 'ready' | 'missing' | 'error';

export interface TwainBridgeStatus {
  status: TwainBridgeStatusKind;
  version?: string;
  sources?: ReadonlyArray<string>;
  selectedSource?: string;
  errorMessage?: string;
}

export interface TwainScanResult {
  image: string;
  mimeType: string;
  width: number;
  height: number;
}

/** Health response shape (see `TwainBridge.cs` `HealthHandler`). */
interface HealthResponse {
  version?: string;
  status?: string;
  sources?: ReadonlyArray<string>;
}

/** Scan response shape (see `TwainBridge.cs` `ScanHandler`). */
interface ScanResponse {
  success: boolean;
  image?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  error?: string;
}

function isMobileUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  // iPadOS 13+ reports as Mac; detect via touch points.
  if (ua.indexOf('Macintosh') >= 0 && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

function pickDefaultSource(sources: ReadonlyArray<string>): string | undefined {
  if (sources.length === 0) return undefined;
  const docketPort = sources.find((s) => s.toLowerCase().indexOf('docketport') >= 0);
  return docketPort ?? sources[0];
}

/** Plain-words advice shown with any scan failure (CHG-0102). */
export const SCAN_RECOVERY_ADVICE =
  'Restart the scanner bridge (tray icon, Exit, then sign out and back in), or use Upload or Camera.';

export interface UseTwainBridgeResult {
  status: TwainBridgeStatus;
  /** Trigger a scan. Returns `null` if the bridge is unavailable or scan failed. */
  scan: (source?: string) => Promise<TwainScanResult | null>;
  /** Why the most recent scan() returned null (the bridge's own error when it gave one); null after a success.
   *  CHG-0102: scan() used to drop the bridge's error, so Scan ID showed nothing. */
  lastScanError: () => string | null;
  /** Change the default scanner source (when multiple are attached). */
  setSelectedSource: (source: string) => void;
  /** Force a re-probe (e.g., after the user installs the bridge and clicks Retry). */
  probe: () => Promise<void>;
}

export function useTwainBridge(): UseTwainBridgeResult {
  const [status, setStatus] = useState<TwainBridgeStatus>({ status: 'checking' });
  const controllers = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    const set = controllers.current;
    return () => {
      set.forEach((c) => {
        try { c.abort(); } catch { /* ignore */ }
      });
      set.clear();
    };
  }, []);

  const makeSignal = useCallback((timeoutMs?: number): AbortSignal => {
    const c = new AbortController();
    controllers.current.add(c);
    if (timeoutMs && timeoutMs > 0) {
      setTimeout(() => {
        try { c.abort(); } catch { /* ignore */ }
      }, timeoutMs);
    }
    return c.signal;
  }, []);

  const probe = useCallback(async (): Promise<void> => {
    if (isMobileUA()) {
      setStatus({ status: 'missing', errorMessage: 'Mobile device — use Camera or Upload.' });
      return;
    }
    setStatus((prev) => ({ ...prev, status: 'checking' }));

    // Single-probe attempt — returns 'success' / 'transient' / 'fatal' so
    // the retry loop knows whether to back off or surface the error
    // immediately. 'transient' covers timeouts + network refusals (bridge
    // may still be coming up); 'fatal' covers HTTP non-200 + zero-source
    // states where retrying won't help.
    type ProbeOutcome =
      | { kind: 'success' }
      | { kind: 'transient'; reason: 'timeout' | 'unreachable' }
      | { kind: 'fatal'; status: TwainBridgeStatus };

    const attempt = async (): Promise<ProbeOutcome> => {
      const signal = makeSignal(HEALTH_TIMEOUT_MS);
      try {
        const resp = await fetch(`${TWAIN_BRIDGE_URL}/health`, { method: 'GET', signal });
        if (!resp.ok) {
          return {
            kind: 'fatal',
            status: { status: 'missing', errorMessage: `Bridge returned HTTP ${resp.status}` },
          };
        }
        const data = (await resp.json()) as HealthResponse;
        // Bridge 1.1.1+ says "not-ready" when it can't scan (session or host window gone, CHG-0102).
        if (data.status === 'not-ready') {
          return {
            kind: 'fatal',
            status: {
              status: 'error',
              ...(data.version ? { version: data.version } : {}),
              errorMessage: `The scanner bridge is running but can't scan right now. ${SCAN_RECOVERY_ADVICE}`,
            },
          };
        }
        const sources = Array.isArray(data.sources) ? data.sources : [];
        const selectedSource = pickDefaultSource(sources);
        if (sources.length === 0) {
          return {
            kind: 'fatal',
            status: {
              status: 'error',
              ...(data.version ? { version: data.version } : {}),
              errorMessage: 'Bridge responded but no TWAIN sources are available.',
            },
          };
        }
        const next: TwainBridgeStatus = { status: 'ready', sources };
        if (data.version) next.version = data.version;
        if (selectedSource) next.selectedSource = selectedSource;
        setStatus(next);
        return { kind: 'success' };
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return { kind: 'transient', reason: 'timeout' };
        }
        return { kind: 'transient', reason: 'unreachable' };
      }
    };

    let lastTransient: 'timeout' | 'unreachable' = 'unreachable';
    for (let i = 0; i <= HEALTH_RETRY_COUNT; i++) {
      const outcome = await attempt();
      if (outcome.kind === 'success') return;
      if (outcome.kind === 'fatal') {
        setStatus(outcome.status);
        return;
      }
      lastTransient = outcome.reason;
      if (i < HEALTH_RETRY_COUNT) {
        await new Promise((r) => setTimeout(r, HEALTH_RETRY_BACKOFF_MS));
      }
    }
    setStatus({
      status: 'missing',
      errorMessage:
        lastTransient === 'timeout'
          ? 'Bridge health check timed out.'
          : 'Bridge not reachable on localhost:18625.',
    });
  }, [makeSignal]);

  // Probe once on mount.
  useEffect(() => {
    probe().catch(() => { /* probe already catches */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastError = useRef<string | null>(null);
  const fail = (message: string): null => {
    lastError.current = message;
    return null;
  };

  const scan = useCallback(
    async (source?: string): Promise<TwainScanResult | null> => {
      if (status.status !== 'ready') {
        return fail(status.errorMessage ?? 'The scanner bridge is not available.');
      }
      const useSource = source ?? status.selectedSource ?? null;
      const signal = makeSignal();
      try {
        const resp = await fetch(`${TWAIN_BRIDGE_URL}/scan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: useSource }),
          signal,
        });
        if (!resp.ok) return fail(`The scanner bridge answered HTTP ${resp.status}.`);
        const data = (await resp.json()) as ScanResponse;
        if (!data.success || !data.image) return fail(data.error || 'The scanner returned no image.');
        lastError.current = null;
        return {
          image: data.image,
          mimeType: data.mimeType ?? 'image/jpeg',
          width: data.width ?? 0,
          height: data.height ?? 0,
        };
      } catch {
        return fail('The scanner bridge could not be reached.');
      }
    },
    [status, makeSignal],
  );

  const lastScanError = useCallback((): string | null => lastError.current, []);

  const setSelectedSource = useCallback((source: string): void => {
    setStatus((prev) => (prev.status === 'ready' ? { ...prev, selectedSource: source } : prev));
  }, []);

  return { status, scan, lastScanError, setSelectedSource, probe };
}
